import React from "react";
import Head from "../../../layout/head/Head";
import Content from "../../../layout/content/Content";
import { Block, BlockBetween, BlockDes, BlockHead, BlockHeadContent, BlockTitle, PreviewCard, Row, Col } from "../../../components/Component";

const quickStartSteps = [
  "Choisir le site sur lequel vous voulez travailler.",
  "Creer ou consulter les observations HSE du terrain.",
  "Suivre le workflow pour traiter les actions correctives.",
  "Utiliser les modules Analytics et IA predictive pour anticiper les risques.",
  "Generer des rapports pour les revues de pilotage et les audits.",
];

const moduleDefinitions = [
  {
    title: "Tableau de bord",
    description: "Vue synthese des indicateurs HSE, des tendances et des priorites de suivi.",
    objective: "Piloter la performance HSE en temps reel.",
    includes: [
      "Indicateurs cles (nombre d observations, statuts, severite).",
      "Vision manageriale des points critiques a traiter en priorite.",
      "Suivi des evolutions dans le temps pour voir les progres ou derives.",
    ],
    benefits: "Aide la direction et les responsables HSE a prendre des decisions rapides et basees sur des donnees.",
  },
  {
    title: "Observations HSE",
    description: "Enregistrement des situations positives, dangereuses, quasi-accidents et non-conformites.",
    objective: "Capturer le terrain de facon structuree et traçable.",
    includes: [
      "Creation d observations avec type, severite, description et site.",
      "Association de pieces jointes et informations de contexte.",
      "Consultation centralisee de toutes les observations de l entreprise.",
    ],
    benefits: "Permet de detecter tot les risques et d eviter la perte d informations terrain.",
  },
  {
    title: "Workflow",
    description: "Suivi des etapes de traitement d une observation, de la soumission a la cloture.",
    objective: "Assurer que chaque observation soit traitee jusqu a resolution.",
    includes: [
      "Changement de statut selon les etapes du processus.",
      "Historique des actions pour la traçabilite des decisions.",
      "Vision des dossiers en retard pour relancer au bon moment.",
    ],
    benefits: "Responsabilise les acteurs et reduit les oublis de traitement.",
  },
  {
    title: "Processus HSE",
    description: "Gestion operationnelle des inspections, permis de travail, MOC, incidents et actifs.",
    objective: "Centraliser les operations HSE critiques dans un seul espace.",
    includes: [
      "Inspections terrain et suivi des reponses conformes/non conformes.",
      "Permis de travail (PTW) pour controler les travaux a risque.",
      "MOC pour encadrer les changements techniques/organisationnels.",
      "Incidents et RCA pour analyser les causes et prevenir la recurrence.",
    ],
    benefits: "Uniformise les pratiques et renforce la maitrise operationnelle des risques.",
  },
  {
    title: "IA predictive",
    description: "Analyse des tendances pour identifier les scenarios de risque et appuyer la prevention.",
    objective: "Anticiper les situations dangereuses avant qu elles ne deviennent des incidents.",
    includes: [
      "Selection d un site puis d une observation pour une analyse ciblee.",
      "Generation d hypotheses de risques, zones sensibles et actions preventives.",
      "Synthese exploitable pour preparer les actions terrain.",
    ],
    benefits: "Aide a prioriser les efforts de prevention avec une approche proactive.",
  },
  {
    title: "Analytics et rapports",
    description: "Mesure de la performance HSE et generation de rapports mensuels, sites ou personnalises.",
    objective: "Transformer les donnees HSE en informations de pilotage lisibles.",
    includes: [
      "Tableaux analytiques pour suivre les tendances et niveaux de risque.",
      "Rapports periodiques pour les comites de pilotage.",
      "Exports adaptes aux besoins d audit ou de communication interne.",
    ],
    benefits: "Facilite les revues de performance et la communication vers les parties prenantes.",
  },
  {
    title: "Conformite et audit",
    description: "Traçabilite des actions, suivi ISO et preparation des preuves pour les audits.",
    objective: "Demontrer la conformite et preparer les audits plus rapidement.",
    includes: [
      "Audit trail des operations realisees dans l application.",
      "Suivi de la traçabilite par objet (observation, incident, action).",
      "Consolidation des elements utiles pour les audits et controles.",
    ],
    benefits: "Reduit le temps de preparation d audit et renforce la confiance des evaluateurs.",
  },
  {
    title: "Sites et zones",
    description: "Organisation des donnees par site et visualisation des zones de risque.",
    objective: "Structurer les informations selon la realite terrain de chaque site.",
    includes: [
      "Gestion du referentiel des sites et de leurs caracteristiques.",
      "Affectation des utilisateurs et des activites par site.",
      "Identification des zones a risque pour cibler les actions preventives.",
    ],
    benefits: "Ameliore la precision des analyses et la pertinence des plans d action.",
  },
];

const glossary = [
  { term: "Observation", definition: "Constat terrain saisi dans l application pour signaler une situation HSE." },
  { term: "Quasi-accident (Near miss)", definition: "Evenement sans dommage mais qui aurait pu provoquer un accident." },
  { term: "Non-conformite", definition: "Ecart par rapport a une exigence interne, legale ou normative." },
  { term: "Action corrective", definition: "Action mise en place pour supprimer la cause d un probleme observe." },
  { term: "MOC", definition: "Management of Change: processus de maitrise des changements techniques et organisationnels." },
  { term: "RCA", definition: "Root Cause Analysis: methode d analyse des causes racines d un incident." },
  { term: "PTW", definition: "Permit To Work: autorisation de travail formalisee pour des operations a risque." },
  { term: "Score de risque", definition: "Indicateur combinant plusieurs facteurs pour prioriser les risques." },
];

const DocumentationPage = () => {
  return (
    <React.Fragment>
      <Head title="Documentation application HSE" />
      <Content>
        <BlockHead size="lg">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>Documentation</BlockTitle>
              <BlockDes>
                <p>
                  Ce module explique les termes, les fonctionnalites et l utilite de chaque module de l application,
                  avec un langage simple pour faciliter la prise en main par tous les profils.
                </p>
              </BlockDes>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <Row className="g-gs">
            <Col lg="6">
              <PreviewCard className="h-100">
                <div className="card-inner">
                  <h6 className="title mb-3">Demarrage rapide</h6>
                  <ul className="list list-checked">
                    {quickStartSteps.map((step, index) => (
                      <li key={index}>{step}</li>
                    ))}
                  </ul>
                </div>
              </PreviewCard>
            </Col>
            <Col lg="6">
              <PreviewCard className="h-100">
                <div className="card-inner">
                  <h6 className="title mb-3">A qui sert cette documentation ?</h6>
                  <p className="mb-2">
                    Cette page aide les utilisateurs metier, managers, equipes HSE et nouveaux arrivants a comprendre
                    l application sans connaissance informatique avancee.
                  </p>
                  <p className="mb-0 text-soft">
                    Elle peut etre enrichie progressivement avec vos procedures internes, captures d ecran et bonnes
                    pratiques.
                  </p>
                </div>
              </PreviewCard>
            </Col>
          </Row>
        </Block>

        <Block>
          <PreviewCard>
            <div className="card-inner">
              <h6 className="title mb-3">Ce que fait chaque module</h6>
              <Row className="g-3">
                {moduleDefinitions.map((moduleItem) => (
                  <Col md="6" key={moduleItem.title}>
                    <div className="border rounded p-3 h-100">
                      <h6 className="mb-2">{moduleItem.title}</h6>
                      <p className="mb-2 text-soft">{moduleItem.description}</p>
                      <p className="mb-2">
                        <strong>Objectif :</strong> {moduleItem.objective}
                      </p>
                      <p className="mb-1">
                        <strong>Fonctionnalites principales :</strong>
                      </p>
                      <ul className="mb-2 ps-3">
                        {moduleItem.includes.map((feature, index) => (
                          <li key={index}>{feature}</li>
                        ))}
                      </ul>
                      <p className="mb-0">
                        <strong>Benefice metier :</strong> {moduleItem.benefits}
                      </p>
                    </div>
                  </Col>
                ))}
              </Row>
            </div>
          </PreviewCard>
        </Block>

        <Block>
          <PreviewCard>
            <div className="card-inner">
              <h6 className="title mb-3">Glossaire des termes HSE</h6>
              <Row className="g-3">
                {glossary.map((item) => (
                  <Col md="6" key={item.term}>
                    <div className="border rounded p-3 h-100">
                      <h6 className="mb-1">{item.term}</h6>
                      <p className="mb-0 text-soft">{item.definition}</p>
                    </div>
                  </Col>
                ))}
              </Row>
            </div>
          </PreviewCard>
        </Block>
      </Content>
    </React.Fragment>
  );
};

export default DocumentationPage;
